<script setup lang="ts">
  import type { ChatMessage } from '@/composables/useChatStream'
  import ChatAssistantMessage from '@/components/app/chat/messages/ChatAssistantMessage.vue'
  import ChatContextMessage from '@/components/app/chat/messages/ChatContextMessage.vue'
  import ChatErrorMessage from '@/components/app/chat/messages/ChatErrorMessage.vue'
  import ChatUserMessage from '@/components/app/chat/messages/ChatUserMessage.vue'
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

  const props = defineProps<{
    messages: ChatMessage[]
    error?: string | null
    sessionUuid?: string
    /** Status label shown on the streaming loader (e.g. "Lese Dokumente..."). */
    streamingLabel?: string
    /**
     * When true, the scroll container fills the full available width (so the scrollbar
     * sits at the viewport edge) while the message column is centered and capped at
     * ``max-w-4xl``. Used by :class:`SidePanelChat` when the panel is reshaped into the
     * fullscreen overlay layout. Default ``false`` preserves the dense side-panel layout.
     */
    centeredContent?: boolean
  }>()

  defineEmits<{
    (e: 'retry'): void
    (e: 'openPreferencesDialog'): void
  }>()

  const lastMessageId = computed(() => {
    const msgs = props.messages
    return msgs.length > 0 ? msgs[msgs.length - 1].id : null
  })

  const scrollContainer = ref<HTMLElement | null>(null)
  const isOverflowing = ref(false)
  const distanceFromBottom = ref(0)

  /**
   * Height (px) over which the bottom fade ramps from invisible to fully
   * opaque. Matches the overlay's own h-6 so the fade feels symmetric with
   * the gradient itself.
   */
  const FADE_RAMP_PX = 24

  // Drives the bottom-fade opacity: invisible at the very bottom of the
  // thread (so the last message is never dimmed) and ramps up to 1 once the
  // user has scrolled away from the bottom by FADE_RAMP_PX.
  const overlayOpacity = computed(() => {
    if (!isOverflowing.value) return 0
    return Math.min(1, distanceFromBottom.value / FADE_RAMP_PX)
  })

  function scrollToBottom() {
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight
    }
  }

  function updateScrollState() {
    const el = scrollContainer.value
    if (!el) return
    isOverflowing.value = el.scrollHeight > el.clientHeight + 1
    distanceFromBottom.value = Math.max(0, el.scrollHeight - el.clientHeight - el.scrollTop)
  }

  let resizeObserver: ResizeObserver | null = null

  onMounted(() => {
    const el = scrollContainer.value
    if (!el) return
    resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(el)
    el.addEventListener('scroll', updateScrollState, { passive: true })
    updateScrollState()
  })

  onBeforeUnmount(() => {
    resizeObserver?.disconnect()
    resizeObserver = null
    scrollContainer.value?.removeEventListener('scroll', updateScrollState)
  })

  // Auto-scroll on new messages or streaming content updates
  watch(
    () => props.messages.length,
    async () => {
      await nextTick()
      scrollToBottom()
      updateScrollState()
    }
  )

  // Also scroll when the last message is streaming (content updates)
  watch(
    () => {
      const last = props.messages[props.messages.length - 1]
      return last?.isStreaming ? last.content.length : -1
    },
    async (len) => {
      if (len > 0) {
        await nextTick()
        scrollToBottom()
        updateScrollState()
      }
    }
  )
</script>

<template>
  <div class="relative flex min-h-0 flex-1 flex-col">
    <div
      ref="scrollContainer"
      class="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
      :class="centeredContent ? '' : 'gap-4 px-4 py-4'"
      data-cy="chat-messages-scroll"
    >
      <div
        :class="
          centeredContent
            ? 'mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 py-6 sm:px-6'
            : 'contents'
        "
      >
        <template
          v-for="msg in messages"
          :key="msg.id"
        >
          <ChatContextMessage
            v-if="msg.role === 'context' && msg.contextData"
            :context-data="msg.contextData"
          />
          <ChatUserMessage
            v-else-if="msg.role === 'user'"
            :message="msg"
          />
          <ChatAssistantMessage
            v-else-if="msg.role === 'assistant'"
            :message="msg"
            :session-uuid="sessionUuid"
            :streaming-label="msg.id === lastMessageId ? streamingLabel : undefined"
            @open-preferences-dialog="$emit('openPreferencesDialog')"
          />
        </template>

        <ChatErrorMessage
          v-if="error"
          :error="error"
          @retry="$emit('retry')"
        />
      </div>
    </div>

    <div
      v-show="isOverflowing"
      :style="{ opacity: overlayOpacity }"
      class="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white to-transparent
        transition-opacity duration-150"
      aria-hidden="true"
    />
  </div>
</template>
